import { inject, Injectable } from '@angular/core';
import { FormArray, FormBuilder } from '@angular/forms';
import { HardwareProduct } from '@app/shared/models/hardware-product.model';
import { NotificationService } from '@core/services/notification.service';
import { StateHelperService } from '@core/services/state-helper.service';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import { MAX_NEW_EXTRA_SIMS_PER_TYPE_IN_ORDER, SimCardType, SimCardVariant } from '@sales/models/simcard.model';
import { CustomerStateModel } from '@sales/modules/details/models/customer.model';
import { CustomerSelectors } from '@sales/modules/details/states/customer.selectors';
import {
  AdditionalServiceSet,
  AdditionalServiceVoice,
} from '@sales/modules/subscription/models/additional-service-voice.model';
import { VoiceSubscriptionOffering } from '@sales/modules/subscription/models/offerings-response.model';
import { FamilyService } from '@sales/services/family.service';
import { SimService } from '@sales/services/sim.service';
import { ApiGetDeliveryOptionsAction } from '@sales/states/logistics/logistics.actions';
import { AssignedSubscriptionModel } from '@shared/models/assigned-subscription-offering.model';
import { PhoneNumber } from '@shared/models/phone-number.model';
import { Subscriber } from '@shared/models/subscriber.model';
import { Subscription } from '@shared/models/subscription-model';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { Observable } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { MobilePaymentType } from '../../enums/mobile-payment-type';
import { MobilePhoneProduct } from '../../models/mobilephone-product';
import { PrivateCustomer } from '../../modules/details/models/private-customer.model';
import { PortingService, PortingWindowsResponse } from '../../modules/subscription/services/porting.service';
import { SubscriptionHelperService } from '../../services/subscription-helper.service';
import { FeatureGateService } from '@shared/services/featuregate.service';
import { SubscriptionService } from '../../services/subscription.service';
import {
  RemoveAccessoryProductAction,
  SetVoiceProductOnAccessoryProductAction,
} from '../accessory-product/accessory-product.actions';
import {
  RemoveMobileProductAction,
  SetVoiceProductOnMobileProductAction,
} from '../customer-product-mobilephone/customer-product-mobilephone.actions';
import { CustomerServiceProductRemoveAction } from '../customer-service-product/customer-service-product.actions';
import { CustomerServiceProductSelectors } from '../customer-service-product/customer-service-product.selectors';
import { UpdateVoiceFormValidStatusAction } from '../form/form.actions';
import { FormStateModel } from '../form/form.types';
import { ApiGetAdditionalServiceOfferingsAction } from '../offering/offering.actions';
import {
  AddAdditionalServiceToExistingSubscriptionAction,
  AdditionalServiceAddAction,
  AdditionalServiceRemoveAction,
  AdditionalServiceUpdateICCAction,
  AdditionalServiceUpdateOrderSimAction,
  ApiGetAllSubscriptionOfferingsAction,
  ApiGetAssignedSubscriptionsAction,
  ApiGetAssignedSubscriptionsErrorAction,
  ApiGetAssignedSubscriptionsSuccessAction,
  ApiGetPortingDatesAction,
  ApiGetPortingDatesErrorAction,
  ApiGetPortingDatesSuccessAction,
  ApiGetSubscriberForVoiceSubscriptionAction,
  ApiGetSubscriberForVoiceSubscriptionErrorAction,
  ApiGetSubscriberForVoiceSubscriptionSuccessAction,
  ApiGetSubscriptionCostsAction,
  ApiGetSubscriptionCostsErrorAction,
  ApiGetSubscriptionCostsSuccessAction,
  ApiGetSubscriptionOfferingsAction,
  ApiGetSubscriptionOfferingsErrorAction,
  ApiGetSubscriptionOfferingsSuccessAction,
  ApiGetSubscriptionServicesAction,
  ApiGetSubscriptionServicesErrorAction,
  ApiGetSubscriptionServicesSuccessAction,
  CombineExistingSubscriptionServicesWithOfferingServices,
  CustomerProductsMoreThanAllowedAction,
  DelayESimPorting,
  LoadingPortingDatesAction,
  ProductAddAction,
  ProductPhoneNumberUpdateAction,
  ProductRemoveConfirmAction,
  ProductResetAction,
  ProductSubscriberUpdateAction,
  RemoveAdditionalServiceFromExistingSubscriptionAction,
  RemoveExistingProductAction,
  ReplaceSimCardAction,
  ResetAdditionalServicesChangesOnExistingSubscriptionAction,
  ResetOfferingOnExistingSubscriptionAction,
  ResetSelectedSubscriptionAction,
  SetAccessoryProductIdOnVoiceProductAction,
  SetAssignedSubscriptionAction,
  SetExistingVoiceSubscriptionsAndAddNewSubscriptionsAction,
  SetMobileProductOnVoiceProductAction,
  SetSelectedStepIndexAction,
  SetSelectedSubscriptionIdAction,
  ToggleSelectedSubscriptionAction,
  UpdateAdditionalServiceToExistingSubscriptionAction,
  UpdateCustomerServiceProductsAction,
  UpdateWhitelistEntriesAction,
  VoiceProductUpdateAction,
  VoiceProductUpdateSponsorAction,
  VoiceProductUpdateSponsorTypeAction,
} from './customer-product.actions';

export interface CustomerProductsStateModel {
  products: VoiceProduct[];
  isLoadingExistingProductDetails: boolean;
  selectedSubscriptionId: string;
  selectedStepIndex: number;
  isLoadingPortingDates: boolean;
  portingDates: PortingWindowsResponse;
  isLoadingAssignedSubscriptions: boolean;
  nextInstanceId: number;
}

enum Messages {
  ICCNotValid = 'ICC er ikke gyldig!',
  ErrorOnFetchingExistingSubscriptions = 'Feil ved henting av eksisterende abonnement.',
  ErrorOnFetchingExistingSubscriptionPrices = 'Feil ved henting av eksisterende kostnader til abonnement.',
  ErrorOnFetchingSubscriptions = 'Feil ved henting av abonnementer',
  ErrorOnFetchingConnectedAdditionalServices = 'Feil ved henting av tilleggstjenester for eksisterende abonnement.',
  ErrorOnFetchingUserOfExistingSubscriptions = 'Feil ved henting av bruker for eksisterende abonnement.',
  ErrorOnFetchingPortingDates = 'Feil ved henting av portingsdatoer',
}

const initialCustomerProductState = {
  products: [],
  isLoadingExistingProductDetails: false,
  selectedSubscriptionId: '',
  selectedStepIndex: 0,
  isLoadingPortingDates: false,
  portingDates: null,
  isLoadingAssignedSubscriptions: false,
  nextInstanceId: 1,
};

const matchesInstance =
  (target: AdditionalServiceVoice) =>
  (service: AdditionalServiceVoice): boolean =>
    SubscriptionHelperService.isSameInstance(service, target);

@State<CustomerProductsStateModel>({
  name: 'customerProducts',
  defaults: initialCustomerProductState,
})
@Injectable()
export class CustomerProductState {
  private formBuilder = inject(FormBuilder);
  private store = inject(Store);
  private simService = inject(SimService);
  private stateHelper = inject(StateHelperService);
  private subscriptionService = inject(SubscriptionService);
  private notificationService = inject(NotificationService);
  private portingService = inject(PortingService);
  private familyService = inject(FamilyService);
  private featureGateService = inject(FeatureGateService);

  formState$: Observable<FormStateModel> = inject(Store).select(state => state.formState);

  private get multipleExtraSims(): boolean {
    return this.featureGateService.isFeatureEnabled('featureMultipleExtraSims');
  }

  @Action(ProductAddAction)
  public addVoiceProduct(
    { getState, setState, dispatch }: StateContext<CustomerProductsStateModel>,
    { productToClone, mobileProductId, accessoryProductId }: ProductAddAction
  ) {
    const state = getState();
    const customer: CustomerStateModel = this.store.selectSnapshot<CustomerStateModel>(s =>
      s.customer ? s.customer : new CustomerStateModel()
    );
    const product = new VoiceProduct();
    product.id = this.stateHelper.getIncrementedProductId(state.products);
    product.isExistingSubscription = false;
    product.mobileProductId = mobileProductId;
    product.accessoryProductId = accessoryProductId;

    if (productToClone) {
      product.offering = productToClone.offering;
      product.subscriber = productToClone.subscriber;
      product.phoneNumber = productToClone.phoneNumber;
      product.additionalServices = productToClone.additionalServices;
    } else {
      product.subscriber = this.setSubscriber(customer.privateCustomer);
      product.phoneNumber = this.setPhoneNumber();
    }

    setState(patch({ products: append([product]) }));

    dispatch([new ToggleSelectedSubscriptionAction(product.id), new SetSelectedStepIndexAction(0)]);
  }

  private setSubscriber(privateCustomer: PrivateCustomer) {
    const subscriber = new Subscriber();

    const { fullname, address, birthdate, city, email, postCode } = privateCustomer;

    subscriber.name = fullname;
    subscriber.address = address;
    subscriber.birthday = birthdate;
    subscriber.city = city;
    subscriber.email = email;
    subscriber.postCode = postCode;

    return subscriber;
  }

  private setPhoneNumber(): PhoneNumber {
    const phoneNumber = new PhoneNumber();

    phoneNumber.isPortingOrder = true;
    phoneNumber.orderSim = false;
    phoneNumber.isESim = false;
    phoneNumber.isOwnerChange = false;
    phoneNumber.keepExistingSim = false;
    phoneNumber.icc = '';
    phoneNumber.aNumber = '';
    phoneNumber.portingTime = '';
    phoneNumber.isEarliestPortingDate = true;

    return phoneNumber;
  }

  @Action(VoiceProductUpdateAction)
  public updateVoiceProduct(
    { getState, setState, dispatch }: StateContext<CustomerProductsStateModel>,
    { offering, productId, changeInfo }: VoiceProductUpdateAction
  ) {
    const state = getState();
    const stateProduct = state.products.find(p => p.id === productId);
    const isOfferingChanged = stateProduct.offering?.id !== offering?.id;

    if (!stateProduct) {
      return;
    }

    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === productId,
          patch({
            id: productId,
            offering: { ...stateProduct.offering, ...offering },
            selectedChangeInfo: changeInfo,
            isAdditionalServiceCampaign: false,
            additionalServices: [] as AdditionalServiceVoice[],
            sponsor: isOfferingChanged ? null : stateProduct.sponsor,
            whitelistEntries: isOfferingChanged ? [] : stateProduct.whitelistEntries,
          })
        ),
      })
    );

    const updatedState = getState();
    const updatedProduct = updatedState.products.find(p => p.id === productId);

    dispatch([
      new ApiGetAdditionalServiceOfferingsAction(updatedProduct.offering.id, updatedProduct.id),
      new UpdateCustomerServiceProductsAction(),
    ]);
  }

  @Action(VoiceProductUpdateSponsorAction)
  public voiceProductUpdateSponsorAction(
    { setState }: StateContext<CustomerProductsStateModel>,
    { productId, sponsor }: VoiceProductUpdateSponsorAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === productId,
          patch({
            id: productId,
            sponsor: sponsor,
          })
        ),
      })
    );
  }

  @Action(VoiceProductUpdateSponsorTypeAction)
  public voiceProductUpdateSponsorTypeAction(
    { setState }: StateContext<CustomerProductsStateModel>,
    { productId, sponsorType }: VoiceProductUpdateSponsorTypeAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === productId,
          patch({
            id: productId,
            sponsor: patch({
              sponsorType: sponsorType,
            }),
          })
        ),
      })
    );
  }

  @Action(ProductSubscriberUpdateAction)
  public updateSubscriberAction(
    { getState, setState }: StateContext<CustomerProductsStateModel>,
    { productId, subscriber }: ProductSubscriberUpdateAction
  ) {
    const state = getState();
    const stateProduct = state.products.find(p => p.id === productId);

    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === productId,
          patch({
            id: productId,
            subscriber: { ...stateProduct?.subscriber, ...subscriber },
          })
        ),
      })
    );
  }

  @Action(ProductPhoneNumberUpdateAction)
  public updatePhoneNumberAction(
    { setState }: StateContext<CustomerProductsStateModel>,
    { productId, phoneNumber }: ProductPhoneNumberUpdateAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === productId,
          patch({
            id: productId,
            phoneNumber: {
              ...phoneNumber,
            },
          })
        ),
      })
    );
  }

  @Action(ProductRemoveConfirmAction)
  public productRemoveConfirmAction(
    { setState, getState, dispatch }: StateContext<CustomerProductsStateModel>,
    { productId }: ProductRemoveConfirmAction
  ) {
    const product = getState().products.find(x => x.id === productId);
    if (product?.isExistingSubscription) {
      dispatch([
        new RemoveExistingProductAction(productId),
        new ResetAdditionalServicesChangesOnExistingSubscriptionAction(productId),
      ]);
      if (product.mobileProductId) {
        const mobileProducts = this.store.selectSnapshot<MobilePhoneProduct[]>(s => s.customerProductsMobilephone.products);
        const mobileProduct = mobileProducts.find(p => p.id === product.mobileProductId);
        if (mobileProduct && mobileProduct.paymentType !== MobilePaymentType.EquipmentUpgrade) {
          // Remove connected mobile product if it is terminal binding, since  it cannot be sold without subscription
          dispatch([
            new SetVoiceProductOnMobileProductAction(product.mobileProductId, null),
            new SetMobileProductOnVoiceProductAction(productId, null),
          ]).subscribe(() => {
            dispatch(new RemoveMobileProductAction(mobileProduct.id));
          });
        }
      }
      if (product.accessoryProductId) {
        const accessoryProducts = this.store.selectSnapshot<HardwareProduct[]>(s => s.customerProductsAccessory.products);
        const accessoryProduct = accessoryProducts.find(p => p.id === product.accessoryProductId);
        if (accessoryProduct) {
          dispatch([
            new SetVoiceProductOnAccessoryProductAction(product.accessoryProductId, null),
            new SetAccessoryProductIdOnVoiceProductAction(productId, null),
          ]).subscribe(() => {
            dispatch(new RemoveAccessoryProductAction(accessoryProduct.id));
          });
        }
      }
    } else {
      const state = getState();

      let formState: FormStateModel;
      let list: FormArray;

      this.formState$.pipe(take(1)).subscribe((value: FormStateModel) => {
        formState = value;
        list = value.subscriptionForm.model.list.filter(p => p.productId !== productId) as any;
      });

      const reducedCustomerProducts = state.products.filter(p => p.id !== productId);
      if (product?.mobileProductId) {
        const mobileProducts = this.store.selectSnapshot<MobilePhoneProduct[]>(s => s.customerProductsMobilephone.products);
        const mobileProduct = mobileProducts.find(p => p.id === product.mobileProductId);
        if (mobileProduct) {
          dispatch(new SetVoiceProductOnMobileProductAction(product.mobileProductId, null)).subscribe(() => {
            // Remove connected mobile product since it cannot be sold without subscription
            dispatch(new RemoveMobileProductAction(mobileProduct.id));
          });
        }
      }

      if (product?.accessoryProductId) {
        const accessoryProducts = this.store.selectSnapshot<HardwareProduct[]>(s => s.customerProductsAccessory.products);
        const accessoryProduct = accessoryProducts.find(p => p.id === product.accessoryProductId);
        if (accessoryProduct) {
          dispatch(new SetVoiceProductOnAccessoryProductAction(product.accessoryProductId, null));
          dispatch(new RemoveAccessoryProductAction(accessoryProduct.id));
        }
      }

      setState({
        ...state,
        products: reducedCustomerProducts,
        selectedSubscriptionId: product?.id === state.selectedSubscriptionId ? '' : state.selectedSubscriptionId,
      });

      const resetSubscriptionForm = list.length === 0;

      dispatch([
        new ApiGetDeliveryOptionsAction(),
        new UpdateVoiceFormValidStatusAction(
          reducedCustomerProducts,
          formState,
          this.formBuilder,
          this.simService,
          this.subscriptionService,
          resetSubscriptionForm
        ),
        new UpdateFormValue({ path: 'formState.subscriptionForm', value: { list } }),
      ]);
    }

    dispatch(new UpdateCustomerServiceProductsAction());
  }

  @Action(UpdateCustomerServiceProductsAction)
  public updateCustomerServiceProducts({ getState }: StateContext<CustomerProductsStateModel>) {
    const customerProducts = getState().products;
    const newCustomerProducts = customerProducts.filter(s => !s.isExistingSubscription);
    const changedCustomerProducts = SubscriptionHelperService.getChangedVoiceSubscriptions(customerProducts);
    const customer = this.store.selectSnapshot(CustomerSelectors.getState);
    const iceTryggStandaloneAddedProduct = this.store.selectSnapshot(
      CustomerServiceProductSelectors.getIceTryggStandaloneAddedProduct
    );
    return this.familyService
      .isCustomerBecomingNewFamilyAutomatically(newCustomerProducts, changedCustomerProducts, customer)
      .pipe(
        tap(isCustomerBecomingNewFamilyAutomatically => {
          if (iceTryggStandaloneAddedProduct && customerProducts.length === 0) {
            this.store.dispatch(new CustomerServiceProductRemoveAction(iceTryggStandaloneAddedProduct.id));
          } else if (iceTryggStandaloneAddedProduct && isCustomerBecomingNewFamilyAutomatically) {
            this.store.dispatch(new CustomerServiceProductRemoveAction(iceTryggStandaloneAddedProduct.id));
          }
        })
      );
  }

  @Action(RemoveExistingProductAction)
  public removeExistingProductAction(
    { setState, getState, dispatch }: StateContext<CustomerProductsStateModel>,
    { productId }: RemoveExistingProductAction
  ) {
    const product = getState().products.find(x => x.id === productId);

    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === productId,
          patch({
            offering: new VoiceSubscriptionOffering(),
            selectedChangeInfo: null,
            subscriber: product.existingSubscriber
              ? product.existingSubscriber
              : ({ name: product.subscription.userName, birthday: product.subscription.userBirthDate } as Subscriber),
            replaceVoiceSimIcc: null,
            replaceVoiceESim: false,
            replaceDataSimIcc: null,
            replaceTwinSimIcc: null,
            replaceAdditionalSimANumber: null,
          })
        ),
      })
    );

    dispatch(new ResetAdditionalServicesChangesOnExistingSubscriptionAction(productId));
  }

  @Action(ResetOfferingOnExistingSubscriptionAction)
  public resetOfferingOnExistingSubscription(
    { setState, dispatch }: StateContext<CustomerProductsStateModel>,
    { productId }: ResetOfferingOnExistingSubscriptionAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === productId,
          patch({ offering: new VoiceSubscriptionOffering(), selectedChangeInfo: null })
        ),
      })
    );

    dispatch(new ResetAdditionalServicesChangesOnExistingSubscriptionAction(productId));
  }

  @Action(AdditionalServiceAddAction)
  public addAdditionalService(
    ctx: StateContext<CustomerProductsStateModel>,
    { id, isMutex, additionalService }: AdditionalServiceAddAction
  ) {
    const state = ctx.getState();
    const product = state.products.find(p => p.id === id);
    const mutexService = isMutex && product.additionalServices.find(x => x.mutexSetName === additionalService.mutexSetName);

    if (isMutex && mutexService) {
      ctx.setState(
        patch({
          products: updateItem<VoiceProduct>(
            p => p.id === id,
            patch({
              id,
              additionalServices: updateItem<AdditionalServiceVoice>(
                service => service.mutexSetName === additionalService.mutexSetName,
                patch({ ...additionalService, instanceId: mutexService.instanceId })
              ),
            })
          ),
        })
      );
    } else {
      const alreadyAdded = product.additionalServices.filter(a => a.productId === additionalService.productId).length;
      const maxCount = this.multipleExtraSims
        ? Math.min(additionalService.availableQuantity ?? 1, MAX_NEW_EXTRA_SIMS_PER_TYPE_IN_ORDER)
        : 1;
      if (alreadyAdded >= maxCount) {
        return;
      }

      const additionalServices = [
        ...product.additionalServices,
        { ...additionalService, instanceId: `${state.nextInstanceId}` },
      ];
      const isAdditionalServiceCampaign = additionalServices.some(x => x.isCampaign);

      ctx.setState(
        patch({
          nextInstanceId: state.nextInstanceId + 1,
          products: updateItem<VoiceProduct>(
            p => p.id === id,
            patch({ id, additionalServices, isAdditionalServiceCampaign })
          ),
        })
      );
    }
  }

  @Action(AddAdditionalServiceToExistingSubscriptionAction)
  public addAdditionalServiceToExistingSubscription(
    { setState, getState }: StateContext<CustomerProductsStateModel>,
    { additionalService, id, isMutex }: AddAdditionalServiceToExistingSubscriptionAction
  ) {
    const state = getState();
    const product = state.products.find(p => p.id === id);
    const mutexService = isMutex && product.additionalServices.find(x => x.mutexSetName === additionalService.mutexSetName);

    if (isMutex && mutexService) {
      setState(
        patch({
          products: updateItem<VoiceProduct>(
            p => p.id === id,
            patch({
              id,
              additionalServices: updateItem<AdditionalServiceVoice>(
                service => service.mutexSetName === additionalService.mutexSetName,
                patch({ ...additionalService })
              ),
            })
          ),
        })
      );
    } else {
      if (this.multipleExtraSims && !SubscriptionHelperService.isServiceActive(additionalService)) {
        const addedNow = product.additionalServices.filter(
          a => a.productId === additionalService.productId && !SubscriptionHelperService.isServiceActive(a)
        ).length;
        if (addedNow >= Math.min(additionalService.availableQuantity ?? 1, MAX_NEW_EXTRA_SIMS_PER_TYPE_IN_ORDER)) {
          return;
        }
      }

      setState(
        patch({
          nextInstanceId: state.nextInstanceId + 1,
          products: updateItem<VoiceProduct>(
            p => p.id === id,
            patch({
              id,
              additionalServices: [
                ...product.additionalServices,
                { ...additionalService, instanceId: additionalService.instanceId ?? `${state.nextInstanceId}` },
              ],
            })
          ),
        })
      );
    }
  }

  @Action(CombineExistingSubscriptionServicesWithOfferingServices)
  public combineActiveSubscriptionAndOfferingServices(
    { setState, getState }: StateContext<CustomerProductsStateModel>,
    { productId, additionalServiceOfferings }: CombineExistingSubscriptionServicesWithOfferingServices
  ) {
    const state = getState();
    const stateProduct = state.products.find(p => p.id === productId);

    if (!stateProduct?.isExistingSubscription) return;

    const originalActiveSubscriptionServices = SubscriptionHelperService.getActiveServices(
      stateProduct.originalSubscriptionServices
    );

    const legacyServiceProducts = () =>
      additionalServiceOfferings.serviceProducts.map(serviceProduct => {
        const existingDataSim = originalActiveSubscriptionServices.find(s => s.isDataSimCard);
        const shouldReturnOldDataSim = serviceProduct.isDataSimCard && existingDataSim && !serviceProduct.isCampaign;

        if (shouldReturnOldDataSim) {
          return existingDataSim;
        }
        const existingTwinSim = originalActiveSubscriptionServices.find(s => s.isTwinSimCard);
        const shouldReturnOldTwinSim = serviceProduct.isTwinSimCard && existingTwinSim && !serviceProduct.isCampaign;

        if (shouldReturnOldTwinSim) {
          return existingTwinSim;
        }
        const activeService = originalActiveSubscriptionServices.find(x => x.productId === serviceProduct.productId);
        if (serviceProduct.isCampaign || !activeService) {
          return serviceProduct;
        }

        return activeService;
      });

    const offeredSims = additionalServiceOfferings.serviceProducts.filter(
      service => SubscriptionHelperService.isAdditionalSim(service) && !service.isCampaign
    );
    const continuedSims = originalActiveSubscriptionServices.filter(
      active =>
        SubscriptionHelperService.isAdditionalSim(active) &&
        offeredSims.some(offered => offered.isDataSimCard === active.isDataSimCard)
    );
    const remainingQuantity = (productId: string) =>
      stateProduct.originalSubscriptionServices?.serviceProducts.find(s => s.productId === productId)?.availableQuantity;

    const combinedServices: AdditionalServiceSet = {
      serviceProducts: !this.multipleExtraSims
        ? legacyServiceProducts()
        : additionalServiceOfferings.serviceProducts
            .map(serviceProduct => {
              if (offeredSims.includes(serviceProduct)) {
                return {
                  ...serviceProduct,
                  availableQuantity: remainingQuantity(serviceProduct.productId) ?? serviceProduct.availableQuantity,
                };
              }
              const activeService = originalActiveSubscriptionServices.find(x => x.productId === serviceProduct.productId);
              if (serviceProduct.isCampaign || !activeService) {
                return serviceProduct;
              }

              return activeService;
            })
            .concat(continuedSims),
      mutexServiceSets: additionalServiceOfferings.mutexServiceSets
        .filter(mutexServiceSet => mutexServiceSet.mutexServices.length > 0)
        .map(mutexServiceSet => ({
          ...mutexServiceSet,
          mutexServices: mutexServiceSet.mutexServices.map(mutexService => {
            const activeService = originalActiveSubscriptionServices.find(x => x.productId === mutexService.productId);

            return activeService ? activeService : mutexService;
          }),
        })),
    };

    setState(
      patch({
        products: updateItem<VoiceProduct>(
          product => product.id === productId,
          patch({
            subscriptionServices: combinedServices,
            additionalServices: SubscriptionHelperService.getActiveServices(combinedServices),
          })
        ),
      })
    );
  }

  @Action(AdditionalServiceUpdateICCAction)
  public additionalServiceUpdateICC(
    ctx: StateContext<CustomerProductsStateModel>,
    action: AdditionalServiceUpdateICCAction
  ) {
    ctx.setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === action.product.id,
          patch({
            additionalServices: updateItem<AdditionalServiceVoice>(
              matchesInstance(action.additionalService),
              patch({
                icc: action.icc,
              })
            ),
          })
        ),
      })
    );
  }

  @Action(AdditionalServiceUpdateOrderSimAction)
  public additionalServiceUpdateOrderSim(
    { setState, getState }: StateContext<CustomerProductsStateModel>,
    { product, additionalService, orderSim }: AdditionalServiceUpdateOrderSimAction
  ) {
    const state = getState();
    const stateProduct = state.products.find(p => p.id === product.id);
    const matches = matchesInstance(additionalService);

    if (stateProduct.additionalServices.find(matches)) {
      setState(
        patch({
          products: updateItem<VoiceProduct>(
            p => p.id === product.id,
            patch({
              additionalServices: updateItem<AdditionalServiceVoice>(
                matches,
                patch({
                  orderSim: orderSim,
                  icc: '',
                })
              ),
            })
          ),
        })
      );
    }
  }

  @Action(UpdateAdditionalServiceToExistingSubscriptionAction)
  public updateAdditionalServiceToExistingSubscription(
    { setState }: StateContext<CustomerProductsStateModel>,
    { id, additionalService, icc, orderSim }: UpdateAdditionalServiceToExistingSubscriptionAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === id,
          patch({
            additionalServices: updateItem<AdditionalServiceVoice>(
              matchesInstance(additionalService),
              patch({
                icc: icc,
                orderSim: orderSim,
              })
            ),
          })
        ),
      })
    );
  }

  @Action(AdditionalServiceRemoveAction)
  public removeAdditionalService(
    { getState, setState }: StateContext<CustomerProductsStateModel>,
    { additionalService, productId }: AdditionalServiceRemoveAction
  ) {
    const state = getState();
    const product = state.products.find(p => p.id === productId);

    const matches = matchesInstance(additionalService);
    const updatedServices = product.additionalServices.filter(a => !matches(a));
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === productId,
          patch({
            id: productId,
            additionalServices: updatedServices,
          })
        ),
      })
    );
  }

  @Action(RemoveAdditionalServiceFromExistingSubscriptionAction)
  public removeAdditionalServiceFromExistingSubscription(
    { getState, setState }: StateContext<CustomerProductsStateModel>,
    { additionalService, id }: RemoveAdditionalServiceFromExistingSubscriptionAction
  ) {
    const state = getState();
    const product = state.products.find(p => p.id === id);

    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === id,
          patch({
            id: id,
            additionalServices: product.additionalServices.filter(a => !matchesInstance(additionalService)(a)),
          })
        ),
      })
    );
  }

  @Action(ApiGetAssignedSubscriptionsAction)
  public getAssignedOfferingForSubscriptions(
    { dispatch, patchState }: StateContext<CustomerProductsStateModel>,
    { subscriptionIds }: ApiGetAssignedSubscriptionsAction
  ) {
    patchState({ isLoadingExistingProductDetails: true });
    return this.subscriptionService.getAssignedSubscriptions(subscriptionIds).pipe(
      tap((assignedSubscriptions: AssignedSubscriptionModel[]) =>
        dispatch(new ApiGetAssignedSubscriptionsSuccessAction(assignedSubscriptions))
      ),
      catchError(() => dispatch(new ApiGetAssignedSubscriptionsErrorAction()))
    );
  }

  @Action(ApiGetAssignedSubscriptionsSuccessAction)
  public getAssignedOfferingForSubscriptionsSuccess(
    { dispatch, patchState }: StateContext<CustomerProductsStateModel>,
    { assignedSubscriptions }: ApiGetAssignedSubscriptionsSuccessAction
  ) {
    const arr = [];

    assignedSubscriptions.forEach(assignedSubscription => {
      dispatch(new SetAssignedSubscriptionAction(assignedSubscription));
      arr.push(new ApiGetSubscriptionCostsAction(assignedSubscription.subscriptionNo));
    });

    return dispatch(arr).pipe(
      tap(() => patchState({ isLoadingExistingProductDetails: false })),
      catchError(() => dispatch(new ApiGetSubscriptionCostsErrorAction()))
    );
  }

  @Action(ApiGetSubscriptionCostsAction)
  public getSubscriptionCosts(
    { dispatch }: StateContext<CustomerProductsStateModel>,
    { subscriptionId }: ApiGetSubscriptionCostsAction
  ) {
    return this.subscriptionService
      .getSubscriptionCost(subscriptionId)
      .pipe(tap(([r]) => dispatch(new ApiGetSubscriptionCostsSuccessAction(subscriptionId, r?.productPrice))));
  }

  @Action(ApiGetSubscriptionCostsSuccessAction)
  public getSubscriptionCostsSuccessAction(
    { setState }: StateContext<CustomerProductsStateModel>,
    { subscriptionId, newPrice }: ApiGetSubscriptionCostsSuccessAction
  ) {
    return setState(
      patch({
        products: updateItem<VoiceProduct>(
          item => item.id.toString() === subscriptionId.toString(),
          item => {
            const newItem = {
              ...item,
              assignedSubscription: { ...item.assignedSubscription, currentPrice: newPrice || null },
            };

            return newItem;
          }
        ),
        isLoadingAssignedSubscriptions: false,
      })
    );
  }

  @Action(SetAssignedSubscriptionAction)
  public setAssignedOfferingOnSubscription(
    { setState }: StateContext<CustomerProductsStateModel>,
    { assignedSubscription }: SetAssignedSubscriptionAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          item => item.id.toString() === assignedSubscription.subscriptionNo.toString(),
          patch({
            assignedSubscription: assignedSubscription,
          })
        ),
      })
    );
  }

  @Action(ApiGetAssignedSubscriptionsErrorAction)
  public getAssignedOfferingForSubscriptionsError({ patchState }: StateContext<CustomerProductsStateModel>) {
    this.notificationService.error(Messages.ErrorOnFetchingExistingSubscriptions);
    patchState({ isLoadingExistingProductDetails: false });
  }

  @Action(ApiGetSubscriptionCostsErrorAction)
  public getSubscriptionCostsError({ patchState }: StateContext<CustomerProductsStateModel>) {
    this.notificationService.error(Messages.ErrorOnFetchingExistingSubscriptionPrices);
    patchState({ isLoadingExistingProductDetails: false });
  }

  @Action(ProductResetAction)
  public productReset(ctx: StateContext<CustomerProductsStateModel>) {
    ctx.patchState(initialCustomerProductState);
  }

  @Action(ApiGetAllSubscriptionOfferingsAction)
  public getAllSubscriptionOfferings({ getState, dispatch }: StateContext<CustomerProductsStateModel>) {
    const { products } = getState();
    products
      .filter(product => product.isExistingSubscription)
      .forEach(product => {
        if (!product.allowedSubscriptionOfferings) {
          dispatch(new ApiGetSubscriptionOfferingsAction(product.subscription.id));
        }
      });
  }

  @Action(ApiGetSubscriptionOfferingsAction)
  public getSubscriptionOfferings(
    { dispatch, setState }: StateContext<CustomerProductsStateModel>,
    { subscriptionId }: ApiGetSubscriptionOfferingsAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          x => x.subscription.id === subscriptionId,
          patch({
            isLoadingSubscriptionOfferings: true,
          })
        ),
      })
    );
    return this.subscriptionService.getOfferingsForVoiceSubscription(subscriptionId).pipe(
      tap(response => dispatch(new ApiGetSubscriptionOfferingsSuccessAction(subscriptionId, response))),
      catchError(() =>
        dispatch(new ApiGetSubscriptionOfferingsErrorAction(subscriptionId, Messages.ErrorOnFetchingSubscriptions))
      )
    );
  }

  @Action(ApiGetSubscriptionOfferingsSuccessAction)
  public getSubscriptionOfferingsResponse(
    { setState }: StateContext<CustomerProductsStateModel>,
    { subscriptionId, offerings }: ApiGetSubscriptionOfferingsSuccessAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          x => x.subscription.id === subscriptionId,
          patch({
            allowedSubscriptionOfferings: offerings,
            isLoadingSubscriptionOfferings: false,
          })
        ),
      })
    );
  }

  @Action(ApiGetSubscriptionOfferingsErrorAction)
  public getSubscriptionOfferingsError(
    { setState }: StateContext<CustomerProductsStateModel>,
    { subscriptionId, error }: ApiGetSubscriptionOfferingsErrorAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          x => x.subscription.id === subscriptionId,
          patch({
            isLoadingSubscriptionOfferings: false,
          })
        ),
      })
    );

    this.notificationService.error(error);
  }

  @Action(ApiGetSubscriptionServicesAction)
  public getSubscriptionServices(
    { dispatch }: StateContext<CustomerProductsStateModel>,
    { subscriptionId }: ApiGetSubscriptionServicesAction
  ) {
    return this.subscriptionService.getAvailableSubscriptionServices(subscriptionId.toString()).pipe(
      map((additionalServiceSet: AdditionalServiceSet) => ({
        serviceProducts: SubscriptionHelperService.withActiveSimInstanceIds(additionalServiceSet.serviceProducts),
        mutexServiceSets: additionalServiceSet.mutexServiceSets.filter(
          mutexServiceSet => mutexServiceSet.mutexServices.length > 0
        ),
      })),
      tap(response => dispatch(new ApiGetSubscriptionServicesSuccessAction(subscriptionId, response))),
      catchError(() => dispatch(new ApiGetSubscriptionServicesErrorAction(subscriptionId)))
    );
  }

  @Action(ApiGetSubscriptionServicesSuccessAction)
  public getSubscriptionServicesSuccess(
    { setState }: StateContext<CustomerProductsStateModel>,
    { subscriptionId, subscriptionServices }: ApiGetSubscriptionServicesSuccessAction
  ) {
    const additionalServices =
      SubscriptionHelperService.getExistingActiveAdditionalServicesOnSubscription(subscriptionServices);

    setState(
      patch({
        products: updateItem<VoiceProduct>(
          product => product.subscription?.id?.toString() === subscriptionId.toString(),
          patch({
            additionalServices: additionalServices,
            subscriptionServices: subscriptionServices,
            originalSubscriptionServices: subscriptionServices,
            isLoadingSubscriptionDetails: false,
          })
        ),
      })
    );
  }

  @Action(ApiGetSubscriptionServicesErrorAction)
  public getSubscriptionServicesError(
    { setState }: StateContext<CustomerProductsStateModel>,
    { subscriptionId }: ApiGetSubscriptionServicesErrorAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          product => product.subscription?.id.toString() === subscriptionId.toString(),
          patch({
            isLoadingSubscriptionDetails: false,
          })
        ),
      })
    );

    this.notificationService.error(Messages.ErrorOnFetchingConnectedAdditionalServices);
  }

  @Action(SetExistingVoiceSubscriptionsAndAddNewSubscriptionsAction)
  public setVoiceSubscriptions(
    { getState, patchState, dispatch }: StateContext<CustomerProductsStateModel>,
    { voiceSubscriptions }: SetExistingVoiceSubscriptionsAndAddNewSubscriptionsAction
  ) {
    const existingVoiceProducts = voiceSubscriptions.map((subscription: Subscription) => {
      const subscriber = new Subscriber();
      subscriber.name = subscription.userName;
      subscriber.birthday = subscription.userBirthDate;

      const existingProduct = new VoiceProduct();
      existingProduct.id = subscription.id;
      existingProduct.isExistingSubscription = true;
      existingProduct.subscription = subscription;
      existingProduct.subscriber = subscriber;

      return existingProduct;
    });
    const newProducts = getState().products;
    patchState({ isLoadingExistingProductDetails: true, products: [...existingVoiceProducts, ...newProducts] });

    existingVoiceProducts.forEach((existingProduct: VoiceProduct) => {
      dispatch(new ApiGetSubscriptionServicesAction(+existingProduct.id));
    });

    dispatch(new ApiGetAssignedSubscriptionsAction(existingVoiceProducts.map(x => x.id.toString())));
  }
  @Action(ResetAdditionalServicesChangesOnExistingSubscriptionAction)
  public resetAdditionalServicesChangesOnExistingSubscription(
    { getState, setState, dispatch }: StateContext<CustomerProductsStateModel>,
    { productId }: ResetAdditionalServicesChangesOnExistingSubscriptionAction
  ) {
    const stateProduct = getState().products.find(p => p.id === productId);
    const state = getState();
    const newAndChangedVoiceProducts = [
      ...SubscriptionHelperService.getChangedVoiceSubscriptions(state.products),
      ...state.products.filter(p => !p.isExistingSubscription),
    ];
    const productInShoppingCart = newAndChangedVoiceProducts.find(p => p.id === productId);

    if (!productInShoppingCart) {
      setState(
        patch({
          products: updateItem<VoiceProduct>(
            product => product.id === productId,
            patch({
              additionalServices: SubscriptionHelperService.getExistingActiveAdditionalServicesOnSubscription(
                stateProduct.originalSubscriptionServices
              ),
              subscriptionServices: stateProduct.originalSubscriptionServices,
            })
          ),
        })
      );

      return;
    }

    const hasChangedSubscription = !!stateProduct.offering.id;
    const subscriptionServices = stateProduct?.subscriptionServices;

    if (subscriptionServices) {
      if (!hasChangedSubscription) {
        dispatch(new RemoveExistingProductAction(productId));
      }

      const additionalServices = hasChangedSubscription
        ? SubscriptionHelperService.getExistingActiveAdditionalServicesOnSubscription(subscriptionServices)
        : SubscriptionHelperService.getExistingActiveAdditionalServicesOnSubscription(
            stateProduct.originalSubscriptionServices
          );

      setState(
        patch({
          products: updateItem<VoiceProduct>(
            product => product.id === productId,
            patch({
              additionalServices: additionalServices,
              subscriptionServices: hasChangedSubscription
                ? subscriptionServices
                : stateProduct.originalSubscriptionServices,
            })
          ),
        })
      );
    }
  }

  @Action(ApiGetSubscriberForVoiceSubscriptionAction)
  public getSubscriberForVoiceSubscription(
    { setState, dispatch }: StateContext<CustomerProductsStateModel>,
    { subscriptionId }: ApiGetSubscriberForVoiceSubscriptionAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          product => product.subscription?.id === subscriptionId,
          patch({ isLoadingExistingSubscriber: true })
        ),
      })
    );

    return this.subscriptionService.getSubscriptionUser(subscriptionId).pipe(
      tap(response => dispatch(new ApiGetSubscriberForVoiceSubscriptionSuccessAction(subscriptionId, response))),
      catchError(() => dispatch(new ApiGetSubscriberForVoiceSubscriptionErrorAction(subscriptionId)))
    );
  }

  @Action(ApiGetSubscriberForVoiceSubscriptionSuccessAction)
  public getSubscriberForVoiceSubscriptionSuccess(
    { setState }: StateContext<CustomerProductsStateModel>,
    { subscriptionId, subscriber }: ApiGetSubscriberForVoiceSubscriptionSuccessAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          product => product.subscription?.id === subscriptionId,
          patch({ existingSubscriber: subscriber, subscriber: subscriber, isLoadingExistingSubscriber: false })
        ),
      })
    );
  }
  @Action(ApiGetSubscriberForVoiceSubscriptionErrorAction)
  public getSubscriberForVoiceSubscriptionError(
    { setState }: StateContext<CustomerProductsStateModel>,
    { subscriptionId }: ApiGetSubscriberForVoiceSubscriptionErrorAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          product => product.subscription?.id === subscriptionId,
          patch({ isLoadingExistingSubscriber: false })
        ),
      })
    );

    this.notificationService.error(Messages.ErrorOnFetchingUserOfExistingSubscriptions);
  }
  @Action(SetMobileProductOnVoiceProductAction)
  public setMobileProductOnVoiceProduct(
    { setState }: StateContext<CustomerProductsStateModel>,
    { productId, mobileProductId }: SetMobileProductOnVoiceProductAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(product => product.id === productId, patch({ mobileProductId: mobileProductId })),
      })
    );
  }

  @Action(ResetSelectedSubscriptionAction)
  public resetAccordion({ patchState }: StateContext<CustomerProductsStateModel>) {
    patchState({ selectedSubscriptionId: '', selectedStepIndex: 0 });
  }

  @Action(ToggleSelectedSubscriptionAction)
  public toggleSelectedSubscription(
    { getState, patchState }: StateContext<CustomerProductsStateModel>,
    { id }: ToggleSelectedSubscriptionAction
  ) {
    patchState({ selectedSubscriptionId: getState().selectedSubscriptionId === id ? '' : id });
  }

  @Action(SetSelectedSubscriptionIdAction)
  public setSelectedSubscriptionId(
    { patchState }: StateContext<CustomerProductsStateModel>,
    { id }: SetSelectedSubscriptionIdAction
  ) {
    patchState({ selectedSubscriptionId: id });
  }

  @Action(SetSelectedStepIndexAction)
  public setSelectedStepIndex(
    { getState, patchState }: StateContext<CustomerProductsStateModel>,
    { index }: SetSelectedStepIndexAction
  ) {
    if (getState().selectedStepIndex !== index) {
      patchState({ selectedStepIndex: index });
    }
  }

  @Action(ApiGetPortingDatesAction)
  public getActiveDates({ dispatch, getState }: StateContext<CustomerProductsStateModel>) {
    if (getState().portingDates) {
      return;
    }

    dispatch(new LoadingPortingDatesAction());

    return this.portingService.getPortingDates().pipe(
      tap((portingDates: PortingWindowsResponse) => dispatch(new ApiGetPortingDatesSuccessAction(portingDates))),
      catchError(() => dispatch(new ApiGetPortingDatesErrorAction()))
    );
  }

  @Action(ApiGetPortingDatesSuccessAction)
  public getActiveDatesSuccess(
    { patchState }: StateContext<CustomerProductsStateModel>,
    { portingDates }: ApiGetPortingDatesSuccessAction
  ) {
    patchState({
      portingDates: portingDates,
      isLoadingPortingDates: false,
    });
  }

  @Action(ApiGetPortingDatesErrorAction)
  public getActiveDatesError({ patchState }: StateContext<CustomerProductsStateModel>) {
    patchState({
      portingDates: null,
      isLoadingPortingDates: false,
    });

    this.notificationService.error(Messages.ErrorOnFetchingPortingDates);
  }

  @Action(ReplaceSimCardAction)
  public replaceVoiceSim(
    { setState, getState }: StateContext<CustomerProductsStateModel>,
    { productId, newIcc, simCardType, simCardVariant, additionalSimANumber }: ReplaceSimCardAction
  ) {
    const stateProduct = getState().products.find(p => p.id === productId);
    if (stateProduct.isExistingSubscription) {
      setState(
        patch({
          products: updateItem<VoiceProduct>(
            p => p.id === productId,
            patch({
              replaceVoiceSimIcc:
                simCardType === SimCardType.Voice && simCardVariant === SimCardVariant.Physical ? newIcc : '',
              replaceVoiceESim: simCardType === SimCardType.Voice && simCardVariant === SimCardVariant.ESim,
              replaceDataSimIcc: simCardType === SimCardType.Data ? newIcc : '',
              replaceTwinSimIcc: simCardType === SimCardType.Twin ? newIcc : '',
              replaceAdditionalSimANumber: additionalSimANumber ?? null,
            })
          ),
        })
      );
    }
  }

  @Action(LoadingPortingDatesAction)
  public loadingPortingDates({ patchState }: StateContext<CustomerProductsStateModel>) {
    patchState({ isLoadingPortingDates: true });
  }

  @Action(UpdateWhitelistEntriesAction)
  public updateWhitelistEntries(
    { setState }: StateContext<CustomerProductsStateModel>,
    { productId, whitelistEntries }: UpdateWhitelistEntriesAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(
          p => p.id === productId,
          patch({
            whitelistEntries: [
              whitelistEntries.whitelistEntry1,
              whitelistEntries.whitelistEntry2,
              whitelistEntries.whitelistEntry3,
            ].filter(e => !!e),
          })
        ),
      })
    );
  }

  @Action(SetAccessoryProductIdOnVoiceProductAction)
  public setAccessoryProductIdOnVoiceProduct(
    { setState }: StateContext<CustomerProductsStateModel>,
    { productId, accessoryProductId }: SetAccessoryProductIdOnVoiceProductAction
  ) {
    setState(
      patch({
        products: updateItem<VoiceProduct>(product => product.id === productId, patch({ accessoryProductId })),
      })
    );
  }

  @Action(DelayESimPorting)
  public delayESimPorting(
    { getState, patchState }: StateContext<CustomerProductsStateModel>,
    { newPortingDate }: DelayESimPorting
  ) {
    patchState({
      products: getState().products?.map(product => {
        return {
          ...product,
          phoneNumber: {
            ...product.phoneNumber,
            activationDateTime:
              product.phoneNumber?.isESim && new Date(product.phoneNumber.activationDateTime) < newPortingDate
                ? newPortingDate
                : product.phoneNumber?.activationDateTime,
          },
        };
      }),
    });
  }

  @Action(CustomerProductsMoreThanAllowedAction)
  public removeSubscriptionsExceedingLimit({ getState, patchState }: StateContext<CustomerProductsStateModel>) {
    const state = getState();
    const numberOfSubscriptionsAllowed = this.store.selectSnapshot<number>(CustomerSelectors.numberOfSubscriptionAllowed);
    patchState({
      products: state.products.slice(0, numberOfSubscriptionsAllowed),
    });
  }
}
